import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Term, TermQuiz } from '../../Data/types';

export interface TermQuizPopupState {
  id: number;
  term: Term;
  quiz: TermQuiz;
}

interface TermQuizPopupProps {
  state: TermQuizPopupState | null;
  onPass: () => void;
  onDismiss: () => void;
}

const LABELS = ['A', 'B', 'C', 'D'];

function fisherYatesShuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export const TermQuizPopup: React.FC<TermQuizPopupProps> = ({ state, onPass, onDismiss }) => {
  // Shuffle once per quiz open (keyed on state.id)
  const shuffled = useMemo(() => {
    if (!state) return null;
    const { quiz } = state;
    const correctOption = quiz.options[quiz.correctIndex];
    const indices = quiz.options.map((_, i) => i);
    const shuffledIndices = fisherYatesShuffle(indices);
    const shuffledOptions = shuffledIndices.map(i => quiz.options[i]);
    const newCorrectIndex = shuffledOptions.indexOf(correctOption);
    return { options: shuffledOptions, correctIndex: newCorrectIndex };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.id]);

  const handleOption = (optionIndex: number) => {
    if (!shuffled) return;
    if (optionIndex === shuffled.correctIndex) {
      onPass();
    } else {
      onDismiss();
    }
  };

  return (
    <AnimatePresence>
      {state && shuffled && (
        <motion.div
          key={state.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.92)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.05 }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 22,
              maxWidth: 340,
              width: '100%',
              padding: '0 24px',
            }}
          >
            {/* Term header */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 44, lineHeight: 1, marginBottom: 6 }}>
                {state.term.emoji}
              </div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: '#e2e8f0',
                  letterSpacing: '0.01em',
                }}
              >
                {state.term.displayName}
              </div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'rgba(251,191,36,0.85)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  marginTop: 4,
                }}
              >
                Mastery Quiz
              </div>
            </div>

            {/* Premise */}
            <div
              style={{
                fontStyle: 'italic',
                color: 'rgba(226,232,240,0.7)',
                fontSize: 13,
                lineHeight: 1.55,
                textAlign: 'center',
                borderLeft: '2px solid rgba(251,191,36,0.45)',
                paddingLeft: 12,
                width: '100%',
              }}
            >
              {state.quiz.premise}
            </div>

            {/* Question */}
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: '#e2e8f0',
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              {state.quiz.question}
            </div>

            {/* Answer buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
              {shuffled.options.map((option, idx) => (
                <motion.button
                  key={idx}
                  onClick={() => handleOption(idx)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '12px 16px',
                    borderRadius: 10,
                    background: '#1e1e3a',
                    border: '1px solid #3d3d5c',
                    color: '#e2e8f0',
                    fontSize: 13,
                    fontWeight: 500,
                    lineHeight: 1.45,
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  <span
                    style={{
                      minWidth: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: 'rgba(99,102,241,0.25)',
                      border: '1px solid rgba(99,102,241,0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#a5b4fc',
                      flexShrink: 0,
                    }}
                  >
                    {LABELS[idx]}
                  </span>
                  <span>{option}</span>
                </motion.button>
              ))}
            </div>

            {/* Dismiss link */}
            <button
              onClick={onDismiss}
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(226,232,240,0.4)',
                fontSize: 12,
                cursor: 'pointer',
                padding: 4,
              }}
            >
              Come back later
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
